import { Button, Flex, type FlexProps, ModalContent, Text } from "@chakra-ui/react";
import { SignPage as ClaimSignPage } from "@components/SendFlow/ClaimSafeSend/SignPage";
import { SignPage as WithdrawSignPage } from "@components/SendFlow/WithdrawSafeSend/SignPage";
import { SafeSendOnIcon } from "@icons";
import { useColor } from "@styles/useColor";
import { type Transaction } from "@umami/chains";
import { useDynamicModalContext } from "@umami/components";
import { useAppSelector } from "@umami/state";
import { capitalize } from "lodash";

import { ModalCloseButton } from "@/components/CloseButton";
import { ShareSecretKeyBox } from "@/components/SafeSend";

export const SafeSendInfo = ({ operation, ...props }: { operation: Transaction } & FlexProps) => {
  const color = useColor();
  const secrets = useAppSelector(state => state.safeSendSecrets.secrets);
  const { openWith } = useDynamicModalContext();

  const isWithdraw = operation.action === "send";
  const hash = operation.parameter.value.hash || operation.parameter.value.hash_;
  const hasRevealButton = isWithdraw && secrets[hash];
  const isClaimable = operation.action === "claim";
  const isOpen = operation.safeSendStatus === "open";
  const hasWithdrawButton = isWithdraw || isClaimable;

  const withdrawButtonText = isOpen
    ? isWithdraw
      ? "Withdraw"
      : "Claim"
    : capitalize(operation.safeSendStatus);

  const handleClick = () => {
    if (!isOpen) {
      return;
    }

    return openWith(
      isWithdraw ? (
        <WithdrawSignPage operation={operation} />
      ) : (
        <ClaimSignPage operation={operation} />
      )
    );
  };

  return (
    <Flex gap="8px" {...props}>
      <Flex width="fit-content" background={color("100")} borderRadius="80px">
        <Flex
          alignItems="center"
          justifyContent="flex-start"
          gap="4px"
          padding="4px  10px"
          paddingRight={hasRevealButton ? "5px" : "10px"}
        >
          <SafeSendOnIcon width="16px" color={color("blue", "black")} />
          <Text color={color("600")} fontSize="14px" fontWeight={600} lineHeight="22px">
            Safe send
          </Text>
        </Flex>
        {hasRevealButton && (
          <Button
            height="full"
            paddingRight="10px"
            paddingLeft="5px"
            color={color("blue", "black")}
            fontSize="14px"
            fontWeight={600}
            borderLeftRadius={0}
            onClick={() =>
              openWith(
                <ModalContent>
                  <ModalCloseButton />
                  <ShareSecretKeyBox safeSendHash={hash} secret={secrets[hash]} />
                </ModalContent>
              )
            }
            size="md"
            variant="ghost"
          >
            Reveal Key
          </Button>
        )}
      </Flex>
      {hasWithdrawButton && (
        <Button
          height="full"
          padding="0 10px"
          fontSize="14px"
          fontWeight={600}
          isDisabled={!isOpen}
          onClick={handleClick}
          size="md"
          variant="secondary"
        >
          {withdrawButtonText}
        </Button>
      )}
    </Flex>
  );
};
